import { OnchainKitProvider } from '@coinbase/onchainkit'
import { FundButton } from '@coinbase/onchainkit/fund'
import { useAutoGenerateCoinbaseSessionToken } from '@components/Web3/Onramp/useAutoGenerateCoinbaseSessionToken'
import { useUsdcTestnetFaucet } from '@components/Web3/UserOpTxModal/usdc/hooks/useUsdcTestnetFaucet'
import { Box, Button, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useEnvironment } from 'hooks/useEnvironment'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { BASE_SEPOLIA } from 'use-towns-client'
import { env } from 'utils/environment'

import { TipOption } from './types'

export const FundSmartAccountOptions = (props: {
    enableUsdc: boolean | undefined
    setTipValue: (tipValue: TipOption | undefined) => void
    onInsufficientBalance?: () => void
    setFundWalletModalType: (type: 'usdc-crypto' | 'eth' | undefined) => void
}) => {
    const { enableUsdc, setTipValue, onInsufficientBalance, setFundWalletModalType } = props
    const environment = useEnvironment()
    const isTestnet = environment.baseChain.id === BASE_SEPOLIA
    return (
        <Box centerContent gap="md">
            <Text strong textAlign="center">
                {`You don't have enough ${enableUsdc ? 'USDC' : 'ETH'}`}
            </Text>
            {enableUsdc ? (
                <Box gap="sm">
                    <Button
                        tone="cta1"
                        rounded="md"
                        size="button_sm"
                        icon="qrCode"
                        onClick={() => {
                            setTipValue(undefined)
                            onInsufficientBalance?.()
                            setFundWalletModalType('usdc-crypto')
                        }}
                    >
                        Receive USDC
                    </Button>
                    {isTestnet && (
                        <Box gap="xs">
                            <FaucetUSDCButton
                                setTipValue={setTipValue}
                                onInsufficientBalance={onInsufficientBalance}
                            />
                            <Text size="xs" color="negative" textAlign="center">
                                No deposit on testnet.
                            </Text>
                        </Box>
                    )}
                    <DepositUSDCButton
                        setTipValue={setTipValue}
                        disabled={isTestnet}
                        onInsufficientBalance={onInsufficientBalance}
                    />
                </Box>
            ) : (
                <Button
                    tone="cta1"
                    rounded="md"
                    size="button_sm"
                    onClick={() => {
                        setTipValue(undefined)
                        onInsufficientBalance?.()
                        setFundWalletModalType('eth')
                    }}
                >
                    Add Funds
                </Button>
            )}
        </Box>
    )
}

export function FaucetUSDCButton(props: {
    setTipValue: (tipValue: TipOption | undefined) => void
    onInsufficientBalance?: () => void
}) {
    const { setTipValue, onInsufficientBalance } = props
    const { data: myAbstractAccount } = useMyAbstractAccountAddress()
    const faucetUSDC = useUsdcTestnetFaucet()
    if (!myAbstractAccount) {
        return null
    }
    return (
        <Button
            tone="level2"
            rounded="md"
            size="button_sm"
            onClick={() => {
                setTipValue(undefined)
                onInsufficientBalance?.()
                faucetUSDC()
            }}
        >
            Faucet USDC
        </Button>
    )
}

function DepositUSDCButton(props: {
    setTipValue: (tipValue: TipOption | undefined) => void
    onInsufficientBalance?: () => void
    disabled?: boolean
}) {
    const { setTipValue, onInsufficientBalance } = props
    const { baseChain } = useEnvironment()
    const { token } = useAutoGenerateCoinbaseSessionToken()

    const button = (args: { loading?: boolean; onClick?: (e: React.MouseEvent) => void }) => (
        <Button
            tone="level2"
            rounded="md"
            size="button_sm"
            icon={!args.loading ? 'creditCard' : undefined}
            disabled={args.loading || props.disabled}
            onClick={(e: React.MouseEvent) => {
                args.onClick?.(e)
            }}
        >
            {args.loading && <ButtonSpinner color="gray2" />}
            Deposit USDC
        </Button>
    )

    if (!token) {
        return button({ loading: true, onClick: undefined })
    }

    return (
        <OnchainKitProvider apiKey={env.VITE_COINBASE_API_KEY} chain={baseChain}>
            <FundButton
                sessionToken={token}
                render={(coinbaseProps) =>
                    button({
                        onClick: (e: React.MouseEvent) => {
                            setTipValue(undefined)
                            onInsufficientBalance?.()
                            coinbaseProps.onClick(e)
                        },
                    })
                }
            />
        </OnchainKitProvider>
    )
}
